import { ChangeEvent, useContext } from "react";
import { FormControlContext } from "../../FormControl/FormControlContext";
import { DEFAULT_COMPONENTS, SelectBaseProps } from "../base/SelectWrapBase/SelectBaseWrap";
import { SelectChip, SelectChipRemove } from "./SelectWrapChip";
import { SelectBaseWrap } from "../base/SelectWrapBase/SelectBaseWrap";

export type MultiSelectWrapProps<TOption = unknown> = SelectBaseProps<TOption> & {
  withMarkAll?: boolean;
  setOptions?: (options: TOption[]) => void;
  labelKey?: keyof TOption;
  dataCy?: string;
};

const components = {
  ...DEFAULT_COMPONENTS,
  MultiValue: SelectChip,
  MultiValueRemove: SelectChipRemove,
};

export const MultiSelectWrap = <TOption,>({
  name: nameProps,
  disabled: disabledProps,
  onBlur: onBlurProps,
  onChange: onChangeProps,
  value: valueProps,
  withMarkAll = false,
  setOptions,
  dataCy,
  labelKey = "name" as keyof TOption,
  ...props
}: MultiSelectWrapProps<TOption>) => {
  const formControl = useContext(FormControlContext);

  const name = nameProps ?? formControl.name;
  const disabled = disabledProps ?? formControl.disabled;
  const onBlur = onBlurProps ?? formControl.onBlur;
  const onChange = onChangeProps ?? formControl.onChange;
  const value = valueProps ?? formControl.value;

  const isDirty = !!formControl.error;

  const markAllOptions = [
    { uuid: "", [labelKey]: "Marcar todos" } as TOption,
    { uuid: "", [labelKey]: "Desmarcar todos" } as TOption,
  ];

  if (withMarkAll) {
    props.options = [...markAllOptions, ...(props.options ?? [])];
  }

  const onMarkAllChange = (selectedOptions: TOption[]) => {
    const shouldMarkAll = selectedOptions.find((o) => o[labelKey] === "Marcar todos");
    const shouldUnmarkAll = selectedOptions.find((o) => o[labelKey] === "Desmarcar todos");

    if (shouldUnmarkAll) {
      setOptions?.([]);
    } else if (shouldMarkAll) {
      setOptions?.(props.options?.filter((o) => !markAllOptions.includes(o)) ?? []);
    } else {
      setOptions?.(selectedOptions as TOption[]);
    }

    onChange?.(selectedOptions as unknown as ChangeEvent<HTMLInputElement>);
  };

  const customFilterOption = (option: any, inputValue: string) => {
    // Sempre mostrar as opções de "Marcar todos" e "Desmarcar todos"
    const name = option.data?.[labelKey] || option.data?.name;
    if (name === "Marcar todos" || name === "Desmarcar todos") {
      return true;
    }

    // Para outras opções, usar o filtro padrão
    const label = option.label ?? name ?? "";
    return String(label).toLowerCase().includes(inputValue.toLowerCase());
  };

  return (
    <SelectBaseWrap
      {...props}
      isMulti
      dataCy={dataCy}
      name={name}
      disabled={disabled}
      onBlur={onBlur}
      onChange={withMarkAll ? onMarkAllChange : onChange}
      value={value}
      isDirty={isDirty}
      components={components}
      filterOption={withMarkAll ? customFilterOption : undefined}
    />
  );
};

MultiSelectWrap.displayName = "MultiSelectWrap";
